import {
  Button,
  ConfirmationModal,
  InlineMessage,
  Loader,
  ModalContent,
  ModalFooter,
} from '@fountain/fountain-ui-components';
import { DialogContent, Link, Paper, Typography } from '@material-ui/core';
import {
  CheckOutlineIcon,
  CheckCircleOutlineIcon,
  iconVariants,
} from '@fountain/ripple/icon';
import { ApiError } from 'api-clients/monolith';
import { ScoreCardResultsService } from 'api-clients/monolith/services/ScoreCardResultsService';
import React, { useCallback, useEffect, useRef, useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';
import { trackAnalyticsEvent } from 'utils/analytics';

import { messages } from './messages';
import { QuestionField } from './QuestionField';
import { RecommendationField } from './RecommendationField';
import {
  AnswerDraft,
  buildDraft,
  draftHasContent,
  DraftMap,
  isAnswered,
  liveScore,
  missingRequiredIds,
  ScorecardFillContext,
  splitQuestions,
  toAnswersPayload,
} from './scorecardFillForm';
import { useStyles } from './styles';
import { useScorecardResultContext } from './useScorecardResultContext';
import { ValidationError } from './ValidationError';

const AUTOSAVE_DELAY_MS = 1000;

// The backend's discriminator for a lost submission race: the scorecard is
// submitted (by another tab or session), not invalid.
const isAlreadySubmittedError = (error: unknown) =>
  error instanceof ApiError &&
  error.status === 422 &&
  (error.body as { code?: string } | null)?.code === 'already_submitted';

const MODE_LABEL = {
  unweighted: messages.modeScored,
  weighted: messages.modeWeighted,
  text: messages.modeCommentsOnly,
} as const;

const ContextEntry: VFC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => {
  const classes = useStyles();
  return (
    <div>
      <Typography
        variant="caption"
        component="div"
        className={classes.contextLabel}
      >
        {label}
      </Typography>
      <Typography variant="body2" className={classes.contextValue}>
        {value ?? '—'}
      </Typography>
    </div>
  );
};

export interface ScorecardFormProps {
  applicantId: string;
  scorecardTemplateId: string;
  // The modal wraps the form in ModalContent/ModalFooter and closes itself
  // after submission; the standalone page lays it out flat, titles itself,
  // and shows the submitted card in place.
  variant: 'modal' | 'page';
  onCancel?: () => void;
  onSubmitted?: () => void;
}

// The shared scorecard fill form: loads the evaluator's context, autosaves
// answers as they change, validates required questions on submit, and
// finalizes through the one-way submit endpoint.
export const ScorecardForm: VFC<ScorecardFormProps> = ({
  applicantId,
  scorecardTemplateId,
  variant,
  onCancel,
  onSubmitted,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const templateId = Number(scorecardTemplateId);
  const { result: contextResult } = useScorecardResultContext(
    templateId,
    applicantId,
  );

  const [draft, setDraft] = useState<DraftMap>({});
  const [restored, setRestored] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saveState, setSaveState] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  const epochRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const draftRef = useRef<DraftMap>({});
  const saveInFlightRef = useRef(false);
  const pendingSaveRef = useRef<DraftMap | null>(null);

  const context =
    contextResult.status === 'ready'
      ? (contextResult.data.score_card as unknown as ScorecardFillContext)
      : undefined;

  useEffect(() => {
    if (!context) return;
    const initial = buildDraft(context.result?.answers ?? []);
    draftRef.current = initial;
    setDraft(initial);
    setSubmitted(context.result?.status === 'submitted');
    const hasSavedAnswers =
      context.result?.status === 'draft' && draftHasContent(initial);
    setRestored(hasSavedAnswers);
    setSaveState(hasSavedAnswers ? 'saved' : 'idle');
    // Runs once per load: `context` flips from undefined exactly when the
    // fetch settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextResult.status]);

  const persistDraft = useCallback(
    async (draftToSave: DraftMap) => {
      // One request at a time: overlapping saves could commit out of order
      // and persist a stale answer set. A save requested mid-flight waits
      // and goes out (latest draft only) once the current one settles.
      if (saveInFlightRef.current) {
        pendingSaveRef.current = draftToSave;
        return;
      }
      saveInFlightRef.current = true;
      epochRef.current += 1;
      const epoch = epochRef.current;
      setSaveState('saving');
      try {
        await ScoreCardResultsService.patchInternalApiScoreCardsTemplatesApplicantsResult(
          templateId,
          applicantId,
          { score_card_result: { answers: toAnswersPayload(draftToSave) } },
        );
        // A response from a superseded save must not overwrite newer state.
        if (epoch === epochRef.current) setSaveState('saved');
      } catch (error) {
        if (isAlreadySubmittedError(error)) {
          epochRef.current += 1;
          setSubmitted(true);
        } else if (epoch === epochRef.current) {
          setSaveState('error');
        }
      } finally {
        saveInFlightRef.current = false;
        const pending = pendingSaveRef.current;
        pendingSaveRef.current = null;
        if (pending) void persistDraft(pending);
      }
    },
    [templateId, applicantId],
  );

  const scheduleSave = useCallback(
    (next: DraftMap) => {
      draftRef.current = next;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = undefined;
        void persistDraft(draftRef.current);
      }, AUTOSAVE_DELAY_MS);
    },
    [persistDraft],
  );

  // Closing the modal or navigating away must not drop a pending change.
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        void persistDraft(draftRef.current);
      }
    },
    [persistDraft],
  );

  const handleAnswerChange = useCallback(
    (questionId: number, patch: AnswerDraft) => {
      setDraft(previous => {
        const next = {
          ...previous,
          [questionId]: { ...previous[questionId], ...patch },
        };
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  if (contextResult.isError) {
    const body = (
      <InlineMessage
        type="red"
        message={<FormattedMessage {...messages.loadFailed} />}
      />
    );
    return variant === 'modal' ? <ModalContent>{body}</ModalContent> : body;
  }

  if (!context) {
    const body = (
      <div className={classes.loading}>
        <Loader block size="2rem" />
      </div>
    );
    return variant === 'modal' ? <ModalContent>{body}</ModalContent> : body;
  }

  const { regular, recommendation } = splitQuestions(
    context.template.questions,
  );
  const missingRegular = missingRequiredIds(regular, draft);
  const recommendationMissing = Boolean(
    recommendation?.required &&
    !isAnswered(recommendation, draft[recommendation.id]),
  );

  const handleSubmitClick = () => {
    setSubmitFailed(false);
    if (missingRegular.length > 0 || recommendationMissing) {
      setShowErrors(true);
      trackAnalyticsEvent('scorecards:fill_form_blocked', {
        context: variant,
        missing_count: missingRegular.length,
        recommendation_missing: recommendationMissing,
      });
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    try {
      await ScoreCardResultsService.postInternalApiScoreCardsTemplatesApplicantsResultSubmit(
        templateId,
        applicantId,
        { score_card_result: { answers: toAnswersPayload(draftRef.current) } },
      );
      epochRef.current += 1;
      setConfirmOpen(false);
      setSubmitted(true);
      trackAnalyticsEvent('scorecards:fill_form_submit', {
        context: variant,
        template_id: templateId,
      });
      onSubmitted?.();
    } catch (error) {
      setConfirmOpen(false);
      if (isAlreadySubmittedError(error)) {
        // Another tab or session won the race: the scorecard is submitted,
        // just not with this form's answers. Show the read-only state.
        epochRef.current += 1;
        setSubmitted(true);
      } else if (error instanceof ApiError && error.status === 422) {
        setShowErrors(true);
      } else {
        setSubmitFailed(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submittedCard = (
    <div className={classes.submittedCard} data-testid="scorecard-submitted">
      {variant === 'page' && (
        <span className={classes.submittedBadge} aria-hidden>
          <CheckOutlineIcon className={iconVariants({ size: 'lg' })} />
        </span>
      )}
      <Typography variant="h3" component={variant === 'page' ? 'h1' : 'h2'}>
        <FormattedMessage {...messages.submittedTitle} />
      </Typography>
      <Typography variant="body2" className={classes.submittedBody}>
        <FormattedMessage
          {...messages.submittedBody}
          values={{
            scorecardName: context.template.name,
            applicantName: context.applicant.name,
          }}
        />
      </Typography>
      {variant === 'page' && (
        <Typography variant="body2" className={classes.submittedBody}>
          <FormattedMessage {...messages.submittedClose} />
        </Typography>
      )}
    </div>
  );

  if (submitted) {
    return variant === 'modal' ? (
      <ModalContent>{submittedCard}</ModalContent>
    ) : (
      <Paper elevation={0} className={classes.card}>
        {submittedCard}
      </Paper>
    );
  }

  const header = (
    <div className={classes.header}>
      {variant === 'page' && (
        <Typography variant="h3" component="h1">
          {context.template.name}
        </Typography>
      )}
      {variant === 'page' && (
        <Typography variant="body2" color="textSecondary">
          <FormattedMessage {...messages.autosaveNote} />
        </Typography>
      )}
      {variant === 'page' && restored && (
        <InlineMessage
          type="green"
          message={<FormattedMessage {...messages.draftRestored} />}
        />
      )}
      <div className={classes.contextGrid}>
        <ContextEntry
          label={intl.formatMessage(messages.applicant)}
          value={context.applicant.name}
        />
        <ContextEntry
          label={intl.formatMessage(globalMessages.opening)}
          value={context.opening_title}
        />
        <ContextEntry
          label={intl.formatMessage(messages.scoring)}
          value={intl.formatMessage(MODE_LABEL[context.template.template_type])}
        />
      </div>
      {context.template.template_type !== 'text' && (
        <Typography
          variant="caption"
          color="textSecondary"
          className={classes.scoringNote}
        >
          <FormattedMessage {...messages.scoringNote} />
        </Typography>
      )}
    </div>
  );

  const questionList = (
    <div className={classes.questions}>
      {regular.map((question, index) => (
        <QuestionField
          key={question.id}
          question={question}
          position={index + 1}
          answer={draft[question.id]}
          showError={showErrors && missingRegular.includes(question.id)}
          onChange={handleAnswerChange}
        />
      ))}
      {recommendation && (
        <RecommendationField
          question={recommendation}
          answer={draft[recommendation.id]}
          showError={showErrors && recommendationMissing}
          onChange={handleAnswerChange}
        />
      )}
    </div>
  );

  const runningScore = liveScore(context.template, draft);

  // The score and the scoring note anchor the footer's left edge; errors and
  // the Draft saved indicator sit inline to their right, centered with the
  // buttons.
  const footerInfo = variant === 'modal' &&
    context.template.template_type !== 'text' && (
      <div className={classes.footerInfo}>
        {runningScore !== null && (
          <Typography variant="body2" className={classes.liveScore}>
            <FormattedMessage
              {...(context.template.template_type === 'weighted'
                ? messages.weightedTotal
                : messages.averageScore)}
            />{' '}
            <span className={classes.liveScoreValue}>
              {runningScore.toFixed(2)}
            </span>
          </Typography>
        )}
        <Typography
          variant="caption"
          color="textSecondary"
          className={classes.scoringNote}
        >
          <FormattedMessage {...messages.scoringNote} />
        </Typography>
      </div>
    );

  const footerAlerts = (
    <>
      {showErrors && missingRegular.length > 0 && (
        <ValidationError role="alert">
          <FormattedMessage
            {...messages.missingSummary}
            values={{
              count: missingRegular.length + Number(recommendationMissing),
            }}
          />
        </ValidationError>
      )}
      {showErrors && missingRegular.length === 0 && recommendationMissing && (
        <ValidationError role="alert">
          <FormattedMessage {...messages.recommendationSummary} />
        </ValidationError>
      )}
      {submitFailed && (
        <InlineMessage
          type="red"
          message={<FormattedMessage {...messages.submitFailed} />}
        />
      )}
      {saveState === 'error' && (
        <InlineMessage
          type="red"
          message={<FormattedMessage {...messages.saveFailed} />}
        />
      )}
      {saveState === 'saved' && (
        <Typography variant="body2" className={classes.savedIndicator}>
          <CheckCircleOutlineIcon className={iconVariants({ size: 'sm' })} />
          <FormattedMessage {...messages.draftSaved} />
        </Typography>
      )}
    </>
  );

  const actions = (
    <>
      {variant === 'modal' ? (
        footerInfo || <span className={classes.footerSpacer} />
      ) : (
        <div className={classes.footerStatus}>{footerAlerts}</div>
      )}
      {variant === 'modal' && footerAlerts}
      {variant === 'modal' && (
        <Button autoWidth type="secondary" size="medium" onClick={onCancel}>
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
      )}
      <Button
        autoWidth
        type="primary"
        size="medium"
        onClick={handleSubmitClick}
      >
        <FormattedMessage {...messages.submitScorecard} />
      </Button>
    </>
  );

  const confirmation = confirmOpen && (
    <ConfirmationModal
      modalTitle={intl.formatMessage(messages.confirmTitle)}
      modalContentText={intl.formatMessage(messages.confirmBody)}
      actionButtonText={intl.formatMessage(messages.submitScorecard)}
      cancelButtonText={intl.formatMessage(globalMessages.cancel)}
      hideHeaderIcon
      isSubmitting={submitting}
      handleClose={() => setConfirmOpen(false)}
      handleCancel={() => setConfirmOpen(false)}
      handleSubmit={handleConfirmSubmit}
    />
  );

  if (variant === 'modal') {
    return (
      <>
        <DialogContent
          dividers
          classes={{
            root: classes.modalContent,
            dividers: classes.modalContentDividers,
          }}
        >
          <div className={classes.modalBody}>
            <aside
              className={classes.applicantPanel}
              data-testid="scorecard-applicant-panel"
            >
              <Typography variant="body2" className={classes.sidebarName}>
                {context.applicant.name}
              </Typography>
              {Boolean(context.applicant.email) && (
                <Typography variant="body2">
                  <Link href={`mailto:${context.applicant.email ?? ''}`}>
                    {context.applicant.email}
                  </Link>
                </Typography>
              )}
              {Boolean(context.applicant.phone_number) && (
                <Typography variant="body2" color="textPrimary">
                  {context.applicant.phone_number}
                </Typography>
              )}
              {context.applicant.resume && (
                <Typography variant="body2">
                  <Link
                    href={context.applicant.resume.url}
                    target="_blank"
                    rel="noopener"
                  >
                    {context.applicant.resume.title}
                  </Link>
                </Typography>
              )}
              {Boolean(context.opening_title) && (
                <Typography variant="body2" color="textPrimary">
                  {context.opening_title}
                </Typography>
              )}
              {context.stage?.title && (
                <Typography variant="body2" color="textPrimary">
                  {context.stage.title}
                </Typography>
              )}
            </aside>
            <div className={classes.questionsPane}>{questionList}</div>
          </div>
        </DialogContent>
        <ModalFooter>
          <div className={classes.footer}>{actions}</div>
        </ModalFooter>
        {confirmation}
      </>
    );
  }

  return (
    <>
      <Paper elevation={0} className={classes.card}>
        {header}
      </Paper>
      <Paper elevation={0} className={classes.card}>
        {questionList}
        <div className={classes.footer}>{actions}</div>
      </Paper>
      {confirmation}
    </>
  );
};
